import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@clarix/ui';
import { quickMeetingApi } from './api';
import { CALENDAR_SIGNAL } from './constants';
import { CHIP_COPY } from './copy';
import { errorCode, formatClock } from './format';
import { quickMeetingKeys } from './queries';
import type { ExtendMinutes, WireEvent } from './types';

export function liveActionErrorMessage(error: unknown, fallback: string = CHIP_COPY.liveActionFailed): string {
  return CHIP_COPY.liveActionError[errorCode(error).code] ?? fallback;
}

/**
 * End or extend the live meeting (API-CONTRACT §11.1). On success the events signal re-reads the
 * chip's `/events/next` and the sidebar's Today list, which both listen to it; on failure only the
 * chip is re-read, since its `version_no`, status or `can` is what went stale.
 */
export function useLiveMeetingActions(userId: string | null, slug: string | null, h24: boolean) {
  const client = useQueryClient();
  const onError = (error: unknown) => {
    toast.error(liveActionErrorMessage(error));
    void client.invalidateQueries({ queryKey: quickMeetingKeys.next(userId, slug) });
  };
  const announce = () => window.dispatchEvent(new CustomEvent(CALENDAR_SIGNAL.EVENTS_CHANGED));

  const end = useMutation({
    mutationFn: (event: WireEvent) =>
      quickMeetingApi.endEvent(slug ?? '', event.id, { version_no: event.version_no }),
    onSuccess: () => {
      toast.success(CHIP_COPY.meetingEnded);
      announce();
    },
    onError,
  });

  const extend = useMutation({
    mutationFn: ({ event, minutes }: { event: WireEvent; minutes: ExtendMinutes }) =>
      quickMeetingApi.extendEvent(slug ?? '', event.id, { minutes, version_no: event.version_no }),
    onSuccess: (updated) => {
      toast.success(CHIP_COPY.extendedTo(formatClock(updated.end, h24)));
      announce();
    },
    onError,
  });

  return { end, extend };
}
